import { useState, useCallback, useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { EntityCard } from '../../features/search/cards/EntityCard'
import { Skeleton } from '../../components/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { Pagination } from '../../components/Pagination'
import { VirtualizedList } from '../../components/VirtualizedList'
import { useDebounce } from '../../hooks/useDebounce'
import { BulkActionBar } from '../../features/entity/components/BulkActionBar'
import { EntityComparisonView } from '../../features/entity/components/EntityComparisonView'
import { useEntitySelectionStore } from '../../stores/entitySelectionStore'
import type { SearchResultItem } from '../../stores/searchStore'

/* ───── API Response Types ───── */

interface SearchHit {
  document: Record<string, unknown>
  score: number
  highlight?: Record<string, string>
  highlights?: Array<{
    field: string
    matched_tokens?: string[]
    snippet?: string
  }>
}

interface SearchResults {
  hits: SearchHit[]
  found: number
  page: number
  per_page: number
}

interface SearchResponse {
  entities: SearchResults | null
  groups: SearchResults | null
  messages: SearchResults | null
  facet_counts?: Record<string, { value: string; count: number }[]>
  query_time_ms: number
}

/* ───── Search Params Schema ───── */

interface EntitiesBrowseParams {
  q?: string
  platform?: string
  country?: string
  risk_min?: number
  risk_max?: number
  sort?: string
}

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/entities/')({
  validateSearch: (search: Record<string, unknown>): EntitiesBrowseParams => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    platform: typeof search.platform === 'string' ? search.platform : undefined,
    country: typeof search.country === 'string' ? search.country : undefined,
    risk_min:
      typeof search.risk_min === 'number' || typeof search.risk_min === 'string'
        ? Number(search.risk_min)
        : undefined,
    risk_max:
      typeof search.risk_max === 'number' || typeof search.risk_max === 'string'
        ? Number(search.risk_max)
        : undefined,
    sort: typeof search.sort === 'string' ? search.sort : undefined,
  }),
  component: EntitiesBrowsePage,
})

/* ───── Helpers ───── */

function extractHighlights(hit: SearchHit): Record<string, string> | undefined {
  const result: Record<string, string> = {}
  let hasAny = false
  if (hit.highlights && Array.isArray(hit.highlights)) {
    for (const h of hit.highlights) {
      if (h.snippet) {
        result[h.field] = h.snippet
        hasAny = true
      }
    }
  }
  if (hit.highlight) {
    for (const [field, snippet] of Object.entries(hit.highlight)) {
      if (snippet && !result[field]) {
        result[field] = snippet
        hasAny = true
      }
    }
  }
  return hasAny ? result : undefined
}

function hitsToItems(hits: SearchHit[]): SearchResultItem[] {
  return hits.map((hit) => {
    const doc = hit.document
    return {
      id: String(doc.id ?? ''),
      type: 'entity' as const,
      name: String(doc.name ?? ''),
      country: doc.country as string | undefined,
      platform: doc.platform as string | undefined,
      risk_score: doc.risk_score as number | undefined,
      entity_type: doc.entity_type as string | undefined,
      description: doc.description as string | undefined,
      highlights: extractHighlights(hit),
    }
  })
}

/* ───── Constants ───── */

const PLATFORMS = [
  'telegram', 'whatsapp', 'signal', 'discord', 'facebook',
  'twitter', 'instagram', 'tiktok', 'youtube', 'reddit',
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'name', label: 'Name' },
  { value: 'risk_score', label: 'Risk Score' },
  { value: 'relevance', label: 'Relevance' },
]

/* ───── Page Component ───── */

function EntitiesBrowsePage() {
  const searchParams = Route.useSearch()
  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState(searchParams.q ?? '')
  const [showComparison, setShowComparison] = useState(false)
  const debouncedSearch = useDebounce(searchInput, 300)

  // Bulk selection state
  const selectedIds = useEntitySelectionStore((s) => s.selectedIds)
  const toggleSelection = useEntitySelectionStore((s) => s.toggleSelection)
  const toggleSelectAll = useEntitySelectionStore((s) => s.toggleSelectAll)
  const selectAllActive = useEntitySelectionStore((s) => s.selectAllActive)
  const deselectAll = useEntitySelectionStore((s) => s.deselectAll)

  // Clear selection when search/filters change
  useEffect(() => {
    deselectAll()
  }, [searchParams.q, searchParams.platform, searchParams.country, searchParams.risk_min, searchParams.risk_max, searchParams.sort, deselectAll])

  const query = debouncedSearch || '*'
  const platform = searchParams.platform ?? ''
  const country = searchParams.country ?? ''
  const riskMin = searchParams.risk_min
  const riskMax = searchParams.risk_max
  const sort = searchParams.sort ?? 'newest'

  const numberFormatter = new Intl.NumberFormat()

  // Build API params
  const buildParams = useCallback(
    (page: number): Record<string, string | number | boolean | undefined> => ({
      q: query,
      type: 'entity',
      page,
      per_page: 25,
      sort,
      platform: platform || undefined,
      country: country || undefined,
      risk_min: riskMin !== undefined && riskMin > 0 ? riskMin : undefined,
      risk_max: riskMax !== undefined && riskMax < 100 ? riskMax : undefined,
    }),
    [query, sort, platform, country, riskMin, riskMax],
  )

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['entities-browse', query, sort, platform, country, riskMin, riskMax],
    queryFn: async ({ pageParam = 1, signal }) => {
      const params = buildParams(pageParam as number)
      return api<SearchResponse>('/v1/search', { params, signal })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFound = lastPage.entities?.found ?? 0
      const loadedCount = allPages.reduce(
        (sum, p) => sum + (p.entities?.hits?.length ?? 0),
        0,
      )
      if (loadedCount >= totalFound) return undefined
      return allPages.length + 1
    },
    staleTime: 30_000,
  })

  const results = data?.pages.flatMap((p) => hitsToItems(p.entities?.hits ?? [])) ?? []
  const total = data?.pages[0]?.entities?.found ?? 0

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleCardClick = (id: string) => {
    navigate({ to: '/entities/$id', params: { id } })
  }

  const updateSearch = (updates: Partial<EntitiesBrowseParams>) => {
    navigate({
      to: '/entities',
      search: (prev: EntitiesBrowseParams) => ({ ...prev, ...updates }),
      replace: true,
    })
  }

  const renderItem = useCallback(
    (item: SearchResultItem) => (
      <div className="flex items-start gap-3">
        {/* Selection checkbox */}
        <label className="flex items-center pt-4 shrink-0 cursor-pointer" data-testid={`entity-select-${item.id}`}>
          <input
            type="checkbox"
            checked={selectedIds.has(item.id)}
            onChange={() => toggleSelection(item.id)}
            className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-ring)]"
          />
        </label>
        <div className="flex-1 min-w-0">
          <EntityCard item={item} onClick={handleCardClick} />
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, toggleSelection],
  )

  // Active filter chips
  const activeFilters: { key: string; label: string; onRemove: () => void }[] = []
  if (platform) {
    activeFilters.push({
      key: 'platform',
      label: platform.charAt(0).toUpperCase() + platform.slice(1),
      onRemove: () => updateSearch({ platform: undefined }),
    })
  }
  if (country) {
    activeFilters.push({
      key: 'country',
      label: country,
      onRemove: () => updateSearch({ country: undefined }),
    })
  }
  if (riskMin !== undefined && riskMin > 0) {
    activeFilters.push({
      key: 'risk_min',
      label: `Risk >= ${riskMin}`,
      onRemove: () => updateSearch({ risk_min: undefined }),
    })
  }
  if (riskMax !== undefined && riskMax < 100) {
    activeFilters.push({
      key: 'risk_max',
      label: `Risk <= ${riskMax}`,
      onRemove: () => updateSearch({ risk_max: undefined }),
    })
  }

  return (
    <div className="flex h-full flex-col" data-testid="entities-browse-page">
      {/* Header */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-4">
        <div className="flex items-center justify-between gap-4 max-w-5xl mx-auto">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Entities</h1>
          <div className="flex items-center gap-3">
            {/* Compare toggle */}
            <button
              type="button"
              onClick={() => setShowComparison((prev) => !prev)}
              className={`
                inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors
                ${showComparison
                  ? 'border-[var(--color-accent)] text-[var(--color-accent)] bg-[var(--color-accent)]/5'
                  : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }
              `}
              data-testid="comparison-toggle"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              Compare
            </button>
            {/* Sort */}
            <select
              value={sort}
              onChange={(e) => updateSearch({ sort: e.target.value })}
              className="
                rounded-md border border-[var(--color-border)]
                bg-[var(--color-input)] px-2.5 py-1.5 text-sm
                text-[var(--color-text-primary)]
                focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
              "
              data-testid="entities-sort-select"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-3">
        <div className="max-w-5xl mx-auto">
          <div
            className="
              flex items-center rounded-lg border
              bg-[var(--color-input)] border-[var(--color-border)]
              transition-colors duration-[var(--transition-fast)]
              focus-within:ring-2 focus-within:ring-[var(--color-ring)]
              focus-within:border-[var(--color-accent)]
            "
          >
            <span className="flex items-center pl-4 text-[var(--color-text-muted)]">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                if (e.target.value) {
                  updateSearch({ q: e.target.value })
                } else {
                  updateSearch({ q: undefined })
                }
              }}
              placeholder="Search entities..."
              className="
                w-full bg-transparent outline-none
                h-10 px-3 text-sm
                text-[var(--color-text-primary)]
                placeholder:text-[var(--color-text-muted)]
              "
              data-testid="entities-search-input"
            />
            {searchInput.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('')
                  updateSearch({ q: undefined })
                }}
                className="flex items-center pr-4 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                aria-label="Clear search"
                data-testid="entities-search-clear"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters row */}
      <div className="border-b border-[var(--color-border)] px-6 py-2">
        <div className="flex items-center gap-3 max-w-5xl mx-auto flex-wrap">
          {/* Platform filter */}
          <select
            value={platform}
            onChange={(e) => updateSearch({ platform: e.target.value || undefined })}
            className="
              rounded-md border border-[var(--color-border)]
              bg-[var(--color-input)] px-2.5 py-1.5 text-xs
              text-[var(--color-text-secondary)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
            "
            data-testid="entities-platform-filter"
          >
            <option value="">All Platforms</option>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>

          {/* Country filter */}
          <input
            type="text"
            value={country}
            onChange={(e) => updateSearch({ country: e.target.value || undefined })}
            placeholder="Country code..."
            className="
              rounded-md border border-[var(--color-border)]
              bg-[var(--color-input)] px-2.5 py-1.5 text-xs w-28
              text-[var(--color-text-secondary)]
              placeholder:text-[var(--color-text-muted)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
            "
            data-testid="entities-country-filter"
          />

          {/* Risk min */}
          <input
            type="number"
            min={0}
            max={100}
            value={riskMin ?? ''}
            onChange={(e) =>
              updateSearch({ risk_min: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="Risk min"
            className="
              rounded-md border border-[var(--color-border)]
              bg-[var(--color-input)] px-2.5 py-1.5 text-xs w-20
              text-[var(--color-text-secondary)]
              placeholder:text-[var(--color-text-muted)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
            "
            data-testid="entities-risk-min"
          />

          {/* Risk max */}
          <input
            type="number"
            min={0}
            max={100}
            value={riskMax ?? ''}
            onChange={(e) =>
              updateSearch({ risk_max: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="Risk max"
            className="
              rounded-md border border-[var(--color-border)]
              bg-[var(--color-input)] px-2.5 py-1.5 text-xs w-20
              text-[var(--color-text-secondary)]
              placeholder:text-[var(--color-text-muted)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]
            "
            data-testid="entities-risk-max"
          />

          {/* Active filter chips */}
          {activeFilters.map((chip) => (
            <span
              key={chip.key}
              className="
                inline-flex items-center gap-1 rounded-full border
                border-[var(--color-border)] bg-[var(--color-bg-tertiary)]
                px-2.5 py-0.5 text-xs font-medium
                text-[var(--color-text-secondary)]
              "
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.onRemove}
                className="ml-0.5 inline-flex items-center justify-center rounded-full p-0.5 hover:bg-[var(--color-bg-primary)]/30 transition-colors focus:outline-none"
                aria-label={`Remove ${chip.label} filter`}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-hidden px-6 py-4">
        <div className="max-w-5xl mx-auto h-full">
          {/* Loading skeleton */}
          {isLoading && (
            <div className="space-y-3" data-testid="entities-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                    <Skeleton className="h-10 w-10 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {isError && !isLoading && (
            <EmptyState
              variant="error"
              description={error?.message ?? 'An error occurred while loading entities.'}
            />
          )}

          {/* Empty state */}
          {!isLoading && !isError && results.length === 0 && (
            <EmptyState
              variant="search"
              title="No entities found"
              description="Try adjusting your search or filters."
            />
          )}

          {/* Comparison View */}
          {showComparison && (
            <div className="mb-4">
              <EntityComparisonView
                leftEntityId={Array.from(selectedIds)[0] ?? ''}
                rightEntityId={Array.from(selectedIds)[1] ?? ''}
                onClose={() => setShowComparison(false)}
              />
            </div>
          )}

          {/* Results list */}
          {!isLoading && !isError && results.length > 0 && (
            <>
              <div className="mb-3 flex items-center gap-3" data-testid="entities-results-meta">
                {/* Select all checkbox */}
                <label className="flex items-center gap-2 cursor-pointer" data-testid="select-all-entities">
                  <input
                    type="checkbox"
                    checked={selectAllActive}
                    onChange={() => toggleSelectAll(results.map((r) => r.id))}
                    className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-ring)]"
                  />
                  <span className="text-xs text-[var(--color-text-muted)]">Select all</span>
                </label>
                <span className="text-xs text-[var(--color-text-muted)]">
                  Found {numberFormatter.format(total)} entit{total !== 1 ? 'ies' : 'y'}
                  {selectedIds.size > 0 && ` | ${selectedIds.size} selected`}
                </span>
              </div>
              <VirtualizedList
                items={results}
                renderItem={renderItem}
                estimateSize={120}
                overscan={5}
                onEndReached={handleEndReached}
                isLoading={isFetchingNextPage}
                emptyMessage="No entities to display"
                getItemKey={(index) => results[index]?.id ?? index}
                className="h-[calc(100vh-20rem)]"
              />
              {/* Pagination info for edge case handling */}
              <Pagination
                currentPage={data?.pages.length ?? 1}
                totalItems={total}
                pageSize={25}
                onPageChange={() => {
                  if (hasNextPage && !isFetchingNextPage) fetchNextPage()
                }}
                showPageSizeSelector={false}
                showTotalCount
                className="mt-3"
              />
            </>
          )}
        </div>
      </div>

      {/* Floating Bulk Action Bar */}
      <BulkActionBar />
    </div>
  )
}
